import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Search, Globe, Download, Loader2, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const DATA_TYPES = [
  { value: "Foundation", label: "Foundation" },
  { value: "SR Legacy", label: "SR Legacy" },
  { value: "Survey (FNDDS)", label: "Survey" },
  { value: "Branded", label: "Branded" },
];
const PAGE_SIZES = [50, 100, 200];

export default function UsdaSearchDialog({ onImported }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [types, setTypes] = useState(["Foundation", "SR Legacy", "Survey (FNDDS)"]);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1, current_page: 1 });
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const fetchPage = useCallback(async (overrides = {}) => {
    const qVal = (overrides.q !== undefined ? overrides.q : q).trim();
    const psVal = overrides.pageSize ?? pageSize;
    const pgVal = overrides.page ?? page;
    const tyVal = overrides.types ?? types;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        q: qVal || "*",
        page: String(pgVal),
        page_size: String(psVal),
        data_types: tyVal.join(","),
      });
      const { data: resp } = await api.get(`/foods/usda/search?${params.toString()}`);
      setData(resp);
    } catch (err) {
      toast.error(formatApiError(err));
      setData({ items: [], total: 0, total_pages: 1, current_page: 1 });
    } finally {
      setLoading(false);
    }
  }, [q, pageSize, page, types]);

  // First open or when filters change → reset to page 1
  useEffect(() => {
    if (open) fetchPage({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    fetchPage({ page: 1 });
  };

  const onChangeSize = (n) => {
    setPageSize(n);
    setPage(1);
    fetchPage({ pageSize: n, page: 1 });
  };

  const toggleType = (val) => {
    const next = types.includes(val)
      ? (types.length > 1 ? types.filter((t) => t !== val) : types) // never empty
      : [...types, val];
    if (next === types) return;
    setTypes(next);
    setPage(1);
    fetchPage({ types: next, page: 1 });
  };

  const goPage = (p) => {
    const safe = Math.max(1, Math.min(data.total_pages || 1, p));
    setPage(safe);
    fetchPage({ page: safe });
  };

  const importItem = async (it) => {
    setImportingId(it.fdc_id);
    try {
      await api.post(`/foods/usda/import`, { fdc_id: it.fdc_id });
      toast.success(`Imported: ${it.name}`);
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImportingId(null);
    }
  };

  const reset = () => {
    setQ(""); setPage(1); setPageSize(50);
    setTypes(["Foundation", "SR Legacy", "Survey (FNDDS)"]);
    setData({ items: [], total: 0, total_pages: 1, current_page: 1 });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 rounded-full" data-testid="usda-search-button">
          <Globe size={16} /> Search USDA
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="usda-search-dialog">
        <DialogHeader>
          <DialogTitle>USDA FoodData Central</DialogTitle>
          <DialogDescription>
            Browse and import foods from the official USDA database. Values per 100&nbsp;g.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSearch} className="flex gap-2 items-center" data-testid="usda-search-form">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by name… (leave empty to browse all)"
              className="pl-9"
              data-testid="usda-search-input"
            />
          </div>
          <Button type="submit" disabled={loading} data-testid="usda-search-submit">
            {loading ? <Loader2 size={14} className="animate-spin" /> : "Filter"}
          </Button>
          {q && (
            <Button type="button" variant="ghost" onClick={() => { setQ(""); setPage(1); fetchPage({ q: "", page: 1 }); }} data-testid="usda-search-clear">
              Clear
            </Button>
          )}
        </form>

        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="text-muted-foreground">Type:</span>
          {DATA_TYPES.map((t) => {
            const active = types.includes(t.value);
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => toggleType(t.value)}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input hover:border-primary/50"}`}
                data-testid={`usda-type-${t.value.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`}
              >
                {active && <Check size={10} />}
                {t.label}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-muted-foreground">Per page:</span>
            {PAGE_SIZES.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => onChangeSize(n)}
                className={`rounded-full px-3 py-1 border transition-colors ${pageSize === n ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input hover:border-primary/50"}`}
                data-testid={`usda-pagesize-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="text-xs text-muted-foreground flex items-center justify-between" data-testid="usda-pagination-summary">
          <span>
            {loading ? "Loading…" : `${data.total.toLocaleString()} foods • page ${data.current_page} of ${data.total_pages}`}
          </span>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto border border-border rounded-lg" data-testid="usda-results">
          {loading ? (
            <div className="p-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading USDA results…</div>
          ) : data.items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No matching foods.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted text-left sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 font-semibold">Name</th>
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold text-right">Cal</th>
                  <th className="px-3 py-2 font-semibold text-right">P</th>
                  <th className="px-3 py-2 font-semibold text-right">C</th>
                  <th className="px-3 py-2 font-semibold text-right">F</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((it) => (
                  <tr key={it.fdc_id} className="hover:bg-muted/40" data-testid={`usda-row-${it.fdc_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{it.name}</div>
                      {it.brand && <div className="text-xs text-muted-foreground">{it.brand}</div>}
                      {it.category && <div className="text-[11px] text-muted-foreground">{it.category}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{it.data_type}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{Math.round(it.calories)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{it.protein.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{it.carbs.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{it.fat.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={importingId === it.fdc_id}
                        onClick={() => importItem(it)}
                        className="gap-1"
                        data-testid={`usda-import-${it.fdc_id}`}
                      >
                        {importingId === it.fdc_id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                        Import
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 pt-1" data-testid="usda-pagination-controls">
          <Button type="button" variant="outline" size="sm" disabled={loading || data.current_page <= 1} onClick={() => goPage(data.current_page - 1)} className="gap-1" data-testid="usda-prev-page">
            <ChevronLeft size={14} /> Prev
          </Button>
          <Input
            type="number"
            min={1}
            max={data.total_pages || 1}
            value={page}
            onChange={(e) => setPage(Number(e.target.value) || 1)}
            onKeyDown={(e) => { if (e.key === "Enter") goPage(page); }}
            className="w-20 h-9 text-center"
            data-testid="usda-page-input"
          />
          <span className="text-xs text-muted-foreground">/ {data.total_pages || 1}</span>
          <Button type="button" variant="outline" size="sm" disabled={loading || data.current_page >= data.total_pages} onClick={() => goPage(data.current_page + 1)} className="gap-1" data-testid="usda-next-page">
            Next <ChevronRight size={14} />
          </Button>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Powered by USDA FoodData Central. Energy uses Atwater factors when available; values per 100&nbsp;g.
        </p>
      </DialogContent>
    </Dialog>
  );
}
